import { TF } from 'react-translator-component';

import {
  DOOR_TIMEOUT_MS,
  DoorAccessApi,
  DoorAccessError,
} from './doorAccess.types';

import {
  DoorAttemptResult,
  DoorConfig,
  DoorOpening,
} from '../types/responses/DoorAccess';

interface MockOptions {
  latencyMs?: number;
  now?: () => Date;
}

const NOT_CONFIGURED = 'Door unlock is not configured for this connector';

export const simulateAttempt = (
  url: string,
  durationMs: number,
): DoorAttemptResult => {
  if (url.includes('timeout')) {
    return {
      status: 'TIMEOUT',
      httpStatus: null,
      durationMs: DOOR_TIMEOUT_MS,
      message: 'The controller did not respond in time',
    };
  }
  if (url.includes('offline')) {
    return {
      status: 'NETWORK_ERROR',
      httpStatus: null,
      durationMs,
      message: 'Could not connect to the controller',
    };
  }
  if (url.includes('fail')) {
    return {
      status: 'UNEXPECTED_STATUS',
      httpStatus: 503,
      durationMs,
      message: 'Service Unavailable',
    };
  }
  return {
    status: 'SUCCESS',
    httpStatus: 200,
    durationMs,
    message: null,
  };
};

export const simulateConnection = (
  url: string,
  durationMs: number,
): DoorAttemptResult => {
  const attempt = simulateAttempt(url, durationMs);
  if (attempt.status === 'TIMEOUT' || attempt.status === 'NETWORK_ERROR') {
    return attempt;
  }
  return { status: 'SUCCESS', httpStatus: null, durationMs, message: null };
};

const blankConfig = (): DoorConfig => ({
  enabled: false,
  transport: 'HTTP',
  deviceId: '',
  method: 'POST',
  url: '',
  headers: [],
  bodyType: 'JSON',
  body: '',
  timeoutMs: DOOR_TIMEOUT_MS,
});

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

export const createDoorAccessMock = ({
  latencyMs = 0,
  now = () => new Date(),
}: MockOptions = {}) => {
  const store = new Map<string, DoorConfig>();
  const openings = new Map<string, DoorOpening[]>();
  const keyOf = (pk: string | number, id: number) => `${pk}:${id}`;
  const later = <T>(value: T): Promise<T> =>
    new Promise((resolve) => setTimeout(() => resolve(value), latencyMs));
  const failLater = (message: string): Promise<never> =>
    new Promise((_, reject) =>
      setTimeout(() => reject(new DoorAccessError(message)), latencyMs),
    );

  const api: DoorAccessApi & {
    peek(pk: string | number, id: number): DoorConfig | undefined;
  } = {
    getSummary: (pk) =>
      later(
        Array.from(store.entries())
          .filter(([key]) => key.startsWith(`${pk}:`))
          .map(([key, config]) => ({
            connectorId: Number(key.split(':')[1]),
            enabled: config.enabled,
            configured:
              config.transport === 'MOVE_IOT' ? !!config.deviceId : !!config.url,
          })),
      ),

    getConfig: (pk, id) =>
      later(clone(store.get(keyOf(pk, id)) ?? blankConfig())),

    saveConfig: (pk, id, payload) => {
      const saved: DoorConfig = {
        ...clone(payload),
        timeoutMs: DOOR_TIMEOUT_MS,
      };
      store.set(keyOf(pk, id), saved);
      return later(clone(saved));
    },

    testConfig: (_pk, _id, payload) =>
      later(simulateConnection(payload.url, 340)),

    open: (pk, id, payload) => {
      const config = payload ?? store.get(keyOf(pk, id));
      const target =
        config?.transport === 'MOVE_IOT' ? config.deviceId : config?.url;
      if (!config || (!payload && !config.enabled) || !target)
        return failLater(TF(NOT_CONFIGURED));

      const result = simulateAttempt(config.url, 410);
      const key = keyOf(pk, id);
      const history = openings.get(key) ?? [];
      history.unshift({
        at: now().toISOString(),
        origin: 'DASHBOARD',
        userName: 'Usuário mock',
        status: result.status,
        httpStatus: result.httpStatus,
        durationMs: result.durationMs,
        message: result.message,
      });
      openings.set(key, history.slice(0, 10));
      return later(result);
    },

    getOpenings: (pk, id) => later(clone(openings.get(keyOf(pk, id)) ?? [])),

    peek: (pk, id) => store.get(keyOf(pk, id)),
  };

  return api;
};
